import React from "react";
import { motion } from "framer-motion";
import { HERO } from "@/constants/testIds";
import { ArrowUpRight, Sparkles } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative pt-32 pb-24 lg:pt-44 lg:pb-32 overflow-hidden bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        {/* Left: text */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-8"
        >
          <div className="flex items-center gap-2 mb-8">
            <span className="inline-block h-2 w-2 bg-[#002FA7]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
              Digital Marketing Expert · Available for projects
            </span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-[7.5rem] leading-[0.95] tracking-tighter text-zinc-900">
            I engineer
            <br />
            digital{" "}
            <span className="italic font-display text-[#002FA7]">growth.</span>
          </h1>

          <p className="mt-8 max-w-xl text-lg text-zinc-600 leading-relaxed">
            I&rsquo;m <span className="text-zinc-900 font-semibold">Akshay Salve</span> —
            I help brands turn search, social and ads into a predictable
            pipeline of customers. SEO, PPC, lead gen, and websites that
            actually convert.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#work"
              data-testid={HERO.primaryCta}
              className="group inline-flex items-center gap-3 px-7 py-4 text-sm font-bold uppercase tracking-wider bg-[#002FA7] text-white hover:bg-[#00227A] hover:-translate-y-1 transition-all"
            >
              View Work
              <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform" />
            </a>
            <a
              href="#contact"
              data-testid={HERO.secondaryCta}
              className="inline-flex items-center gap-2 px-7 py-4 text-sm font-bold uppercase tracking-wider border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors"
            >
              Let&rsquo;s Talk
            </a>
          </div>
        </motion.div>

        {/* Right: portrait */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-4 relative"
        >
          <div className="relative aspect-[3/4] w-full overflow-hidden bg-zinc-100">
            <img
              src="https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&w=900&q=85"
              alt="Akshay Salve"
              data-testid={HERO.portrait}
              className="h-full w-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
              loading="eager"
            />
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-white/90 backdrop-blur-sm border-t border-zinc-200 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#002FA7]" />
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-900">
                5+ Years · 50+ Brands
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Marquee strip */}
      <div className="mt-20 lg:mt-28 border-y border-zinc-200 py-6 overflow-hidden bg-white">
        <div className="marquee-track">
          {Array.from({ length: 2 }).map((_, dup) => (
            <div key={dup} className="flex items-center gap-16 px-8 shrink-0">
              {[
                "SEO",
                "Social Media Marketing",
                "Lead Generation",
                "PPC Ads",
                "Website Making",
                "Conversion Rate Optimization",
                "Funnel Strategy",
                "Brand Growth",
              ].map((t, i) => (
                <span
                  key={`${dup}-${i}`}
                  className="font-display text-3xl sm:text-4xl text-zinc-900/15 whitespace-nowrap"
                >
                  {t} <span className="text-[#002FA7]/30 mx-4">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
